/*
 * Purpose: Mission map AI chat — sticky composer adapts to panel resize + viewport scroll
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Sparkles, Send, ChevronRight, Loader2, Radar, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { AVERAGE_OUTCOME_SEEDS } from '@/seeds/averageOutcomes';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import {
  planFlowFromMapChat,
  summarizePlanForChat,
  type MapChatTurn,
} from '@/services/autoblocks/mapAssistant';
import { applyPlanToMap } from '@/lib/applyPlanToMap';
import { getMissionGoalIntent } from '@/lib/mapNodeSummary';
import { invalidateFlowSummaryCache } from '@/services/autoblocks/flowSummary';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useMissionLiveBuildStore } from '@/stores/missionLiveBuildStore';
import { toast } from 'sonner';
import { analyzeInlineValidation, autoFixWorkflowInline, autoFixWorkflowInlineSafe } from '@/services/inlineValidation';
import { InlineValidationInsights } from '@/components/mission/InlineValidationInsights';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import { evaluateFlowTracingTruth } from '@/services/flowTracingTruth';
import { simulateFlowTrace } from '@/services/flowTraceSimulation';
import { useFlowTracingHistoryStore } from '@/stores/flowTracingHistoryStore';
import { useActivityLog } from '@/components/ActivityLog';

type ChatMessage = MapChatTurn & {
  id: string;
  plan?: OutcomePlan;
  applyMode?: 'auto' | 'confirm';
};

const QUICK_PROMPTS = AVERAGE_OUTCOME_SEEDS.slice(0, 3).map((s) => s.outcomeText);

function renderAssistantText(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/** Keeps composer height + keyboard inset in sync when panel or viewport changes */
function useAssistantPanelViewport(
  panelRef: RefObject<HTMLElement | null>,
  composerRef: RefObject<HTMLElement | null>,
  scrollRef: RefObject<HTMLElement | null>
) {
  const [composerHeight, setComposerHeight] = useState(128);
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    const panel = panelRef.current;
    const composer = composerRef.current;
    if (!panel && !composer) return;

    const measureComposer = () => {
      if (!composerRef.current) return;
      const nextHeight = Math.ceil(composerRef.current.getBoundingClientRect().height);
      setComposerHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    const pinScrollIfNearBottom = () => {
      const el = scrollRef.current;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < 64) {
        requestAnimationFrame(() => {
          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
      }
    };

    const onLayoutChange = () => {
      measureComposer();
      pinScrollIfNearBottom();
    };

    onLayoutChange();

    const ro = new ResizeObserver(onLayoutChange);
    if (panel) ro.observe(panel);
    if (composer) ro.observe(composer);

    window.addEventListener('resize', onLayoutChange);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onLayoutChange);
    };
  }, [panelRef, composerRef, scrollRef]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const syncKeyboardInset = () => {
      setKeyboardInset(Math.max(0, window.innerHeight - vv.offsetTop - vv.height));
    };

    syncKeyboardInset();
    vv.addEventListener('resize', syncKeyboardInset);
    vv.addEventListener('scroll', syncKeyboardInset);
    return () => {
      vv.removeEventListener('resize', syncKeyboardInset);
      vv.removeEventListener('scroll', syncKeyboardInset);
    };
  }, []);

  return { composerHeight, keyboardInset };
}

export interface MapAiAssistantProps {
  /** Column layout for resizable panel; floating kept for legacy embeds */
  layout?: 'column' | 'floating';
  className?: string;
  onRequestCollapse?: () => void;
}

export function MapAiAssistant({
  layout = 'column',
  className,
  onRequestCollapse,
}: MapAiAssistantProps) {
  const { currentWorkflow, applyAssembledWorkflowIfCurrent, saveWorkflow, selectNode, updateMissionMeta } = useWorkflowStore();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `I use your **GOAL block** and the **blocks that fulfill it** as context. Ask to change block intent, MCP goals, or wiring — I'll propose edits and **Apply to map** when you approve (or immediately when you say "wire it" / "apply").`,
    },
  ]);
  const [pendingPlan, setPendingPlan] = useState<OutcomePlan | null>(null);
  const [pendingRefinement, setPendingRefinement] = useState(false);
  const [isPlanning, setIsPlanning] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const { addActivity } = useActivityLog();
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const llmReady = hasResolvedSecret('OPENAI_API_KEY');

  const { composerHeight, keyboardInset } = useAssistantPanelViewport(
    panelRef,
    composerRef,
    scrollRef
  );

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const liveBuildOutcome = useMissionLiveBuildStore((s) => s.outcome);
  const liveBuildStatus = useMissionLiveBuildStore((s) => s.status);
  const liveBuildChatSeeded = useMissionLiveBuildStore((s) => s.chatSeeded);
  const inlineValidation = useMemo(
    () => (currentWorkflow ? analyzeInlineValidation(currentWorkflow) : null),
    [currentWorkflow]
  );
  const trace = useExecutionTraceStore((s) =>
    currentWorkflow?.id ? s.traces[currentWorkflow.id] : undefined
  );
  const simulation = useMemo(
    () => (currentWorkflow ? simulateFlowTrace(currentWorkflow) : null),
    [currentWorkflow]
  );
  const liveStatus = trace?.running
    ? 'Live tracing active'
    : trace?.failedNodeId
      ? 'Last run failed at highlighted block'
      : simulation && !simulation.predictedComplete
        ? 'Pre-run simulation predicts flow is incomplete'
      : null;
  const flowTracingMode = currentWorkflow?.missionMeta?.flowTracingMode === true;
  const truthFactor = useMemo(
    () =>
      currentWorkflow ? evaluateFlowTracingTruth(currentWorkflow, trace, inlineValidation, simulation) : null,
    [currentWorkflow, trace, inlineValidation, simulation]
  );
  const compare = useFlowTracingHistoryStore((s) =>
    currentWorkflow?.id ? s.compareToLastGood(currentWorkflow.id) : null
  );
  const pushSnapshot = useFlowTracingHistoryStore((s) => s.pushSnapshot);

  useEffect(() => {
    if (!currentWorkflow || !truthFactor) return;
    const source = trace?.running ? 'run' : simulation && !simulation.predictedComplete ? 'simulation' : 'edit';
    pushSnapshot(currentWorkflow, truthFactor, source);
  }, [currentWorkflow, truthFactor, trace?.running, simulation, pushSnapshot]);

  // Echo the seeding outcome into chat so the map build reads as a conversation.
  useEffect(() => {
    if (!liveBuildOutcome || liveBuildChatSeeded || liveBuildStatus === 'idle') return;
    useMissionLiveBuildStore.getState().markChatSeeded();
    setMessages((prev) => [
      ...prev,
      { id: `lb-u-${Date.now()}`, role: 'user', content: liveBuildOutcome },
      {
        id: `lb-a-${Date.now()}`,
        role: 'assistant',
        content:
          "On it — I'm wiring this onto your map now. **Watch it build**, or hit **Take control** to edit anytime. When you're ready, just tell me what to change.",
      },
    ]);
    scrollToBottom();
  }, [liveBuildOutcome, liveBuildChatSeeded, liveBuildStatus, scrollToBottom]);

  const goalSnippet = getMissionGoalIntent(currentWorkflow);
  const goalPreview =
    goalSnippet.length > 72 ? `${goalSnippet.slice(0, 71)}…` : goalSnippet;

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isPlanning) return;

    const workflowSnapshot = currentWorkflow;
    const targetWorkflowId = workflowSnapshot?.id ?? null;
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    const history = messages.filter((m) => m.id !== 'welcome');
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsPlanning(true);
    setPendingPlan(null);
    setPendingRefinement(false);
    scrollToBottom();

    try {
      const { plan, isRefinement, applyMode, suggestedActions } = await planFlowFromMapChat(
        history,
        trimmed,
        workflowSnapshot
      );
      const summary = summarizePlanForChat(plan, {
        isRefinement,
        priorWorkflow: workflowSnapshot,
        applyMode,
        suggestedActions,
      });

      if (applyMode === 'confirm') {
        setPendingPlan(plan);
        setPendingRefinement(isRefinement);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: summary,
            plan,
            applyMode: 'confirm',
          },
        ]);
        return;
      }

      const { applied, workflow } = applyPlanToMap(plan, {
        expectedWorkflowId: targetWorkflowId,
        refine: isRefinement,
      });

      if (!applied || !workflow) {
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content:
              '**Plan ready** — you switched to a different mission while I was planning, so I did **not** apply it to the map. Switch back to the original mission and send your message again, or paste the plan manually.',
            plan,
          },
        ]);
        toast.warning('Mission changed during planning — map not updated', {
          action: { label: 'Retry', onClick: () => setInput(trimmed) },
        });
        return;
      }

      saveWorkflow();
      if (workflow.id) invalidateFlowSummaryCache(workflow.id);

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: summary,
          plan,
          applyMode: 'auto',
        },
      ]);
      setPendingPlan(null);
      setPendingRefinement(false);
      toast.success(
        isRefinement
          ? `Map updated — ${workflow.nodes.length} blocks (Undo on map toolbar)`
          : `Flow wired — ${workflow.nodes.length} blocks (Undo on map toolbar)`
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Could not plan this flow';
      setMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: msg }]);
      toast.error(msg, {
        action: { label: 'Retry', onClick: () => setInput(trimmed) },
      });
    } finally {
      setIsPlanning(false);
      scrollToBottom();
    }
  }, [
    input,
    isPlanning,
    messages,
    scrollToBottom,
    currentWorkflow,
    saveWorkflow,
  ]);

  const handleApply = useCallback(
    (plan: OutcomePlan, refine = pendingRefinement) => {
      const targetWorkflowId = currentWorkflow?.id ?? null;
      const { applied, workflow } = applyPlanToMap(plan, {
        expectedWorkflowId: targetWorkflowId,
        refine,
      });
      if (!applied || !workflow) {
        toast.warning('Mission changed — could not apply plan to map', {
          action: { label: 'Retry', onClick: () => handleApply(plan, refine) },
        });
        return;
      }
      saveWorkflow();
      if (workflow.id) invalidateFlowSummaryCache(workflow.id);
      setPendingPlan(null);
      setPendingRefinement(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-apply-${Date.now()}`,
          role: 'assistant',
          content: `Applied **${workflow.nodes.length} blocks** and **${workflow.edges.length} links** to your map. Use **Check** then **Start** when ready.`,
        },
      ]);
      toast.success('Flow applied to map');
      scrollToBottom();
    },
    [currentWorkflow?.id, pendingRefinement, saveWorkflow, scrollToBottom]
  );

  const handleAutoFix = useCallback(() => {
    if (!currentWorkflow || autoFixing) return;
    setAutoFixing(true);
    try {
      const safeOnly = currentWorkflow.missionMeta?.smartAutoFixPolicy === 'safe-only';
      const result = safeOnly ? autoFixWorkflowInlineSafe(currentWorkflow) : autoFixWorkflowInline(currentWorkflow);
      if (result.changes.length === 0) {
        toast.message('Smart Blocks found no changes needed');
        return;
      }
      const applied = applyAssembledWorkflowIfCurrent(currentWorkflow.id, result.workflow);
      if (!applied) {
        toast.warning('Mission changed — Smart Blocks was not applied');
        return;
      }
      saveWorkflow();
      invalidateFlowSummaryCache(currentWorkflow.id);
      addActivity(
        'Smart Blocks auto-fix',
        `${result.changes.length} change(s), policy: ${safeOnly ? 'safe-only' : 'safe-and-review'}`,
        'update'
      );
      toast.success(
        `Smart Blocks applied (${result.changes.length} changes · ${result.safetySummary.safeAutoFix} safe auto-fixes)`
      );
    } finally {
      setAutoFixing(false);
    }
  }, [currentWorkflow, autoFixing, applyAssembledWorkflowIfCurrent, saveWorkflow, addActivity]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, []);

  const composer = (
    <div
      ref={composerRef}
      className={cn(
        'z-10 space-y-2 border-t border-border/60',
        'bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/85',
        'p-2 sm:p-3',
        'shadow-[0_-6px_16px_-8px_rgba(0,0,0,0.12)] dark:shadow-[0_-6px_16px_-8px_rgba(0,0,0,0.45)]'
      )}
      style={{
        paddingBottom: `max(0.5rem, calc(env(safe-area-inset-bottom, 0px) + ${keyboardInset}px))`,
      }}
      data-map-assistant-composer
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap gap-1.5">
        {QUICK_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            disabled={isPlanning}
            onClick={() => setInput(prompt)}
            className="text-[10px] sm:text-xs rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:bg-muted/60 truncate max-w-full text-left transition-colors"
          >
            {prompt.length > 48 ? `${prompt.slice(0, 47)}…` : prompt}
          </button>
        ))}
      </div>
      <div className="ui-field-action-group flex items-end gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={focusComposer}
          placeholder="e.g. When Gmail gets job alerts, log applications and remind me to follow up"
          rows={2}
          disabled={isPlanning}
          className="ui-form-control min-h-[2.75rem] max-h-28 resize-none text-sm flex-1"
        />
        <Button
          size="icon"
          disabled={!input.trim() || isPlanning}
          onClick={() => void handleSend()}
          className="ui-field-action-btn shrink-0 ui-lift-accent"
          aria-label="Send to AI assistant"
        >
          {isPlanning ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );

  const messageList = (
    <div
      ref={scrollRef}
      className={cn(
        'min-h-0 overflow-y-auto overscroll-contain p-3 space-y-3',
        layout === 'floating' && 'max-h-[min(42vh,280px)] sm:max-h-[min(48vh,320px)]',
        layout === 'column' && 'h-full'
      )}
      style={{ scrollPaddingBottom: Math.max(composerHeight, 8) }}
    >
      {inlineValidation && flowTracingMode && (
        <div className="rounded-lg border border-border/60 bg-muted/20 p-2">
          <InlineValidationInsights
            analysis={inlineValidation}
            dense
            onFocusNode={(nodeId) => selectNode(nodeId)}
            liveStatus={liveStatus}
            truthFactor={truthFactor}
            compareSummary={
              compare
                ? `Compared to last good: ${Math.round(compare.confidenceDelta * 100)}% confidence, ${compare.nodeDelta} nodes, ${compare.edgeDelta} links`
                : null
            }
          />
        </div>
      )}
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={cn(
            'rounded-lg px-3 py-2 text-sm leading-relaxed',
            msg.role === 'user'
              ? 'ml-2 sm:ml-4 bg-primary/10 text-foreground border border-primary/20'
              : 'mr-1 sm:mr-2 bg-muted/50 text-muted-foreground border border-border/40'
          )}
        >
          <p className="whitespace-pre-wrap">{renderAssistantText(msg.content)}</p>
          {msg.plan && (
            <Button
              size="sm"
              variant={msg.applyMode === 'confirm' ? 'default' : 'outline'}
              className="mt-2 w-full sm:w-auto"
              onClick={() => handleApply(msg.plan!)}
            >
              {msg.applyMode === 'confirm' ? 'Apply to map' : 'Re-apply to map'}
            </Button>
          )}
        </div>
      ))}
      {isPlanning && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1 pb-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Planning from your map and chat…
        </div>
      )}
    </div>
  );

  if (layout === 'floating') {
    return (
      <div
        className={cn(
          'absolute bottom-3 left-1/2 z-30 w-[min(calc(100%-1rem),36rem)] -translate-x-1/2 pointer-events-auto',
          className
        )}
        data-canvas-ui
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          ref={panelRef}
          className="rounded-lg border border-border/80 bg-card/95 backdrop-blur-md shadow-xl overflow-hidden flex flex-col max-h-[70vh]"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm font-semibold truncate">Map AI Assistant</p>
          </div>
          {messageList}
          {composer}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      id="mission-map-assistant"
      className={cn(
        'grid h-full min-h-0 max-h-full overflow-hidden bg-card',
        'grid-rows-[auto_minmax(0,1fr)_auto]',
        className
      )}
      data-canvas-ui
      onPointerDown={(e) => e.stopPropagation()}
    >
      {pendingPlan && (
        <div className="shrink-0 border-t border-primary/25 bg-primary/5 px-3 py-2 flex items-center gap-2">
          <p className="min-w-0 flex-1 text-xs text-foreground truncate">Plan ready — apply to update blocks</p>
          <Button size="sm" className="shrink-0 h-8" onClick={() => handleApply(pendingPlan)}>
            Apply to map
          </Button>
        </div>
      )}

      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            <p className="truncate text-sm font-semibold">Map AI Assistant</p>
            <Badge variant="outline" className="hidden shrink-0 text-[10px] sm:inline-flex">
              {llmReady ? 'AI + rules' : 'Rules planner'}
            </Badge>
          </div>
          <p className="truncate text-[10px] text-muted-foreground" title={goalSnippet}>
            GOAL: {goalPreview}
          </p>
        </div>
        <Button
          variant={flowTracingMode ? 'secondary' : 'ghost'}
          size="sm"
          className="h-8 gap-1.5 px-2 text-[11px]"
          onClick={() => updateMissionMeta({ flowTracingMode: !flowTracingMode })}
          title="Toggle Flow Tracing highlighting"
        >
          <Radar className="h-3.5 w-3.5" />
          {flowTracingMode ? 'Tracing on' : 'Tracing off'}
        </Button>
        {flowTracingMode && (inlineValidation?.suggestions.length ?? 0) > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2 text-[11px]"
            onClick={handleAutoFix}
            disabled={autoFixing}
            title="Smart Blocks — auto fix/configure this flow"
          >
            {autoFixing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <WandSparkles className="h-3.5 w-3.5" />
            )}
            Smart Blocks
          </Button>
        )}
        {onRequestCollapse && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 p-0"
            onClick={onRequestCollapse}
            aria-label="Collapse assistant panel"
            title="Collapse panel"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {messageList}
      {composer}
    </div>
  );
}
